import React from "react";
import { CheckCircle, type LucideIcon } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { cn } from "../../lib/utils";
import { formatPhoneDisplay } from "../../lib/phone-formatter";
import { type BaseComponentProps } from "../../types/puck";
import { type ImagePosition } from "../../lib/image-position";
import { SmartLink } from "../smart-link";
import { Button } from "../ui/button";

export type CTAVariant = "band" | "card" | "split";

export interface CTAProps extends BaseComponentProps {
  headline: string;
  subheadline?: string;
  ctaText: string;
  ctaUrl: string;
  secondaryCtaText?: string;
  secondaryCtaUrl?: string;
  backgroundImage?: string;
  /** Where the background image keeps its focus when the band crops it. */
  imagePosition?: ImagePosition;
  overlay?: "dark" | "gradient" | "primary";
  variant?: CTAVariant;
  showTrustSignals?: "yes" | "no";
  trustSignals?: Array<{ text: string; icon?: string }>;
}

function getBackgroundStyle(
  backgroundImage: string | undefined,
  overlay: CTAProps["overlay"],
  imagePosition?: ImagePosition,
) {
  if (!backgroundImage) return {};

  // "Primary Color" used to paint flat black, which is what "Dark Overlay"
  // already does. Tint with the theme's primary instead, over a dark scrim so
  // the white copy stays readable when a business picks a light primary.
  const primaryTint = "color-mix(in srgb, var(--jobsite-primary) 70%, transparent)";

  const overlays: Record<NonNullable<CTAProps["overlay"]>, string> = {
    dark: "linear-gradient(rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0.72))",
    gradient:
      "linear-gradient(135deg, rgba(5, 24, 17, 0.88) 0%, rgba(19, 79, 48, 0.82) 55%, rgba(0, 0, 0, 0.45) 100%)",
    primary: `linear-gradient(${primaryTint}, ${primaryTint}), linear-gradient(rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.45))`,
  };

  return {
    backgroundImage: `${overlays[overlay || "dark"]}, url(${backgroundImage})`,
    backgroundSize: "cover",
    backgroundPosition: imagePosition || "center",
    backgroundRepeat: "no-repeat",
  };
}

export const CTA: React.FC<CTAProps> = ({
  headline,
  subheadline,
  ctaText,
  ctaUrl,
  secondaryCtaText,
  secondaryCtaUrl,
  backgroundImage,
  imagePosition = "center",
  overlay = "dark",
  variant = "band",
  showTrustSignals = "no",
  trustSignals = [],
  id,
  className,
  puck,
}: CTAProps & {
  puck?: { metadata?: { business?: { phone?: string; telephone?: string } } };
}) => {
  const metadataPhone = puck?.metadata?.business?.phone || puck?.metadata?.business?.telephone;
  const resolvedSecondaryCtaText = metadataPhone
    ? `Call Us: ${formatPhoneDisplay(metadataPhone)}`
    : secondaryCtaText;
  const resolvedSecondaryCtaUrl = metadataPhone
    ? `tel:${metadataPhone.replace(/\D/g, "")}`
    : secondaryCtaUrl;

  const hasBackground = Boolean(backgroundImage);
  // Node-tolerant like Hero: the section editor may inject an editable
  // element in place of the string. String semantics unchanged.
  const activeTrustSignals = Array.isArray(trustSignals)
    ? trustSignals.filter((signal) =>
        typeof signal.text === "string" ? signal.text.trim() : signal.text,
      )
    : [];

  const surfaceClass = cn(
    hasBackground
      ? "jobsite:text-white"
      : overlay === "gradient"
        ? "jobsite:bg-linear-to-br jobsite:from-primary jobsite:via-primary/90 jobsite:to-accent jobsite:text-primary-foreground"
        : overlay === "primary"
          ? "jobsite:bg-primary jobsite:text-primary-foreground"
          : "jobsite:bg-linear-to-br jobsite:from-foreground jobsite:to-foreground/90 jobsite:text-background",
    variant === "split" && "jobsite:py-16 jobsite:md:py-24",
    variant !== "split" && "jobsite:py-20 jobsite:md:py-28",
  );
  const panelClass = cn(
    variant === "card" &&
      "jobsite:mx-auto jobsite:max-w-5xl jobsite:border jobsite:px-6 jobsite:py-8 jobsite:shadow-2xl jobsite:sm:px-8 jobsite:md:px-12 jobsite:md:py-10",
    variant === "card" &&
      (hasBackground
        ? "jobsite:border-white/15 jobsite:bg-white/10 jobsite:backdrop-blur-xl"
        : "jobsite:border-white/10 jobsite:bg-black/10"),
  );
  const primaryButtonClass = cn(
    "jobsite:w-full jobsite:sm:w-auto jobsite:px-7 jobsite:py-6 jobsite:text-base jobsite:font-semibold",
    hasBackground || overlay !== "gradient"
      ? "jobsite:bg-accent jobsite:text-accent-foreground jobsite:hover:bg-accent/90"
      : "jobsite:bg-background jobsite:text-foreground jobsite:hover:bg-background/90",
  );
  const secondaryButtonClass = cn(
    "jobsite:w-full jobsite:sm:w-auto jobsite:px-7 jobsite:py-6 jobsite:text-base jobsite:font-semibold",
    "jobsite:border-2 jobsite:border-current jobsite:bg-black/10 jobsite:backdrop-blur-md jobsite:hover:bg-black/20",
  );
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;

  const renderTrustSignals = (align: "center" | "start") => {
    if (showTrustSignals !== "yes" || activeTrustSignals.length === 0) return null;

    return (
      <div
        className={cn(
          "jobsite:flex jobsite:flex-wrap jobsite:gap-3 jobsite:text-sm",
          align === "center" ? "jobsite:justify-center" : "jobsite:justify-start",
        )}
      >
        {activeTrustSignals.map((signal, idx) => {
          const IconComponent = signal.icon
            ? (LucideIcons as unknown as Record<string, LucideIcon>)[signal.icon] || CheckCircle
            : CheckCircle;

          return (
            <div
              key={idx}
              className={cn(
                "jobsite:flex jobsite:items-center jobsite:gap-2 jobsite:border jobsite:px-3.5 jobsite:py-2",
                hasBackground
                  ? "jobsite:border-white/15 jobsite:bg-white/10"
                  : "jobsite:border-current/10 jobsite:bg-white/10",
              )}
              style={surfaceRadiusStyle}
            >
              <IconComponent className="jobsite:h-4 jobsite:w-4" />
              <span className="jobsite:font-medium">{signal.text}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <section
      id={id}
      className={cn("jobsite:relative jobsite:overflow-hidden", surfaceClass, className)}
      style={getBackgroundStyle(backgroundImage, overlay, imagePosition)}
    >
      <div className="jobsite:container jobsite:relative jobsite:z-10 jobsite:mx-auto jobsite:px-4 jobsite:md:px-6">
        <div className={panelClass} style={variant === "card" ? surfaceRadiusStyle : undefined}>
          {variant === "split" ? (
            <div className="jobsite:grid jobsite:grid-cols-1 jobsite:items-center jobsite:gap-8 jobsite:lg:grid-cols-[minmax(0,1.2fr)_minmax(300px,0.8fr)] jobsite:lg:gap-12">
              <div className="jobsite:space-y-5">
                <h2 className="jobsite:max-w-3xl jobsite:font-heading jobsite:text-4xl jobsite:leading-[0.98] jobsite:tracking-tight jobsite:text-balance jobsite:md:text-5xl jobsite:lg:text-6xl">
                  {headline}
                </h2>
                {subheadline && (
                  <p className="jobsite:max-w-2xl jobsite:text-lg jobsite:leading-relaxed jobsite:opacity-90 jobsite:md:text-xl">
                    {subheadline}
                  </p>
                )}
                {renderTrustSignals("start")}
              </div>

              <div className="jobsite:flex jobsite:flex-col jobsite:gap-3 jobsite:lg:items-stretch">
                <Button asChild size="lg" className={primaryButtonClass}>
                  <SmartLink to={ctaUrl}>{ctaText}</SmartLink>
                </Button>
                {resolvedSecondaryCtaText && resolvedSecondaryCtaUrl && (
                  <Button asChild variant="outline" size="lg" className={secondaryButtonClass}>
                    <SmartLink to={resolvedSecondaryCtaUrl}>{resolvedSecondaryCtaText}</SmartLink>
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="jobsite:mx-auto jobsite:max-w-4xl jobsite:text-center">
              <h2 className="jobsite:font-heading jobsite:text-4xl jobsite:leading-[0.98] jobsite:tracking-tight jobsite:text-balance jobsite:md:text-5xl jobsite:lg:text-6xl">
                {headline}
              </h2>
              {subheadline && (
                <p className="jobsite:mx-auto jobsite:mt-5 jobsite:max-w-2xl jobsite:text-lg jobsite:leading-relaxed jobsite:opacity-90 jobsite:md:text-xl">
                  {subheadline}
                </p>
              )}

              <div className="jobsite:mt-8 jobsite:flex jobsite:flex-col jobsite:items-center jobsite:justify-center jobsite:gap-3 jobsite:sm:flex-row">
                <Button asChild size="lg" className={primaryButtonClass}>
                  <SmartLink to={ctaUrl}>{ctaText}</SmartLink>
                </Button>
                {resolvedSecondaryCtaText && resolvedSecondaryCtaUrl && (
                  <Button asChild variant="outline" size="lg" className={secondaryButtonClass}>
                    <SmartLink to={resolvedSecondaryCtaUrl}>{resolvedSecondaryCtaText}</SmartLink>
                  </Button>
                )}
              </div>

              <div className="jobsite:mt-6">{renderTrustSignals("center")}</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
